import type { Ref, TextareaHTMLAttributes } from "react";
import { useId } from "react";

import type { FormFieldLabelProps } from "@core/components/FormFieldLabel";
import type {
  FormFieldPresentationProps,
  FormInputStatus,
  OmittedCommonFormFieldProps,
} from "@core/components/FormInputWrapper";
import { FormInputWrapper } from "@core/components/FormInputWrapper";
import classnames from "classnames";

import styles from "./TextArea.module.scss";

export type TextAreaStatus = FormInputStatus;

export type TextAreaProps = FormFieldPresentationProps & {
  ref?: Ref<HTMLTextAreaElement>;
  labelFields?: Pick<FormFieldLabelProps, "label" | "requiredText" | "optionalText" | "necessityIndicator">;
} & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, OmittedCommonFormFieldProps>;

export const TextArea = ({
  labelFields,
  labelId,
  value,
  disabled = false,
  readOnly = false,
  status = "default",
  helperText,
  maxLength,
  name,
  required,
  id: externalId,
  ref,
  showCharacterCount,
  ...rest
}: TextAreaProps) => {
  const generatedId = useId();
  const textareaId = externalId ?? generatedId;
  const helperTextId = helperText ? `${textareaId}-supporting` : undefined;

  return (
    <FormInputWrapper
      labelFields={labelFields}
      labelId={labelId}
      disabled={disabled}
      required={required}
      status={status}
      helperText={helperText}
      helperTextId={helperTextId}
      maxLength={maxLength}
      value={value}
      inputId={textareaId}
      showCharacterCount={showCharacterCount ?? maxLength !== undefined}
    >
      <div
        className={classnames(styles.textareaWrapper, {
          [styles.invalid]: status === "error",
          [styles.readOnly]: readOnly,
        })}
      >
        <textarea
          {...rest}
          id={textareaId}
          name={name}
          className={styles.textarea}
          value={value}
          maxLength={maxLength}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          aria-invalid={status === "error" || undefined}
          aria-describedby={helperTextId}
          ref={ref}
        />
      </div>
    </FormInputWrapper>
  );
};
